// Auth state machine for the Mini App, owned by the user entity slice.
//
// State transitions (TASK-029):
//   idle → authenticating → authenticated   (success)
//                       \→ error            (HMAC fail, network fail, 401)
//   authenticated → authenticating          (refresh in flight)
//   error → authenticating                  (user-initiated retry)
//   * → idle                                (signOut / reset)
//
// Token + user live in memory only. Mini App WebViews are ephemeral
// (PRD §8.0.4) so persistence offers no value and dragging in localStorage
// would just leak a bearer token across sessions.

import { create } from "zustand";

import type { AuthUser } from "@community-analytics/shared";

export type AuthStatus =
  | "idle"
  | "authenticating"
  | "authenticated"
  | "error";

export interface AuthState {
  status: AuthStatus;
  token: string | null;
  expiresAt: number | null;
  user: AuthUser | null;
  /**
   * Last error message, populated only when status === "error". Cleared on
   * the next setAuthenticating() so the UI never shows a stale message
   * mid-retry.
   */
  error: string | null;
}

export interface AuthActions {
  setAuthenticating: () => void;
  setAuthenticated: (input: {
    token: string;
    expiresAt: number;
    user: AuthUser;
  }) => void;
  setError: (message: string) => void;
  reset: () => void;
}

export type AuthStore = AuthState & AuthActions;

export const INITIAL_AUTH_STATE: AuthState = {
  status: "idle",
  token: null,
  expiresAt: null,
  user: null,
  error: null,
};

export const useAuthStore = create<AuthStore>((set) => ({
  ...INITIAL_AUTH_STATE,
  setAuthenticating: () =>
    set((s) => ({
      status: "authenticating",
      error: null,
      // keep token + user during refresh — fetch-client is mid-flight and
      // dropping them now would null the Authorization header on the retry.
      token: s.token,
      expiresAt: s.expiresAt,
      user: s.user,
    })),
  setAuthenticated: ({ token, expiresAt, user }) =>
    set({
      status: "authenticated",
      token,
      expiresAt,
      user,
      error: null,
    }),
  setError: (message) =>
    set({
      status: "error",
      token: null,
      expiresAt: null,
      user: null,
      error: message,
    }),
  reset: () => set(INITIAL_AUTH_STATE),
}));

export const selectAuthStatus = (s: AuthStore): AuthStatus => s.status;
export const selectAuthToken = (s: AuthStore): string | null => s.token;
export const selectAuthUser = (s: AuthStore): AuthUser | null => s.user;
export const selectIsAuthed = (s: AuthStore): boolean =>
  s.status === "authenticated" && s.token !== null;
export const selectAuthError = (s: AuthStore): string | null => s.error;
